var _a;
const selector = "img[data-progressive-image]";
const warmCachePrefix = "bear:progressive-image:";
const states = /* @__PURE__ */ new WeakMap();
const nearby = /* @__PURE__ */ new Set();
const failedImages = /* @__PURE__ */ new Set();
let firstImage;
let interacted = Boolean(window.__bearPlaybackIntent || ((_a = window.Shopify) == null ? void 0 : _a.designMode));
let ready = false;
let scheduled = false;
let active = 0;
let observer;
let resizeObserver;
let cancelInitialUpgrade;
function selectInitialContentImage() {
  var _a2;
  const root = document.getElementById("MainContent");
  if (!root || ((_a2 = window.Shopify) == null ? void 0 : _a2.designMode) || !("IntersectionObserver" in window)) return;
  const images = [...root.querySelectorAll("img")];
  if (!images.length) return;
  const initialObserver = new IntersectionObserver((entries) => {
    var _a3;
    stop();
    let largest;
    let largestArea = 0;
    for (const { target: img, intersectionRect, isIntersecting } of entries) {
      if (!isIntersecting || !img.isConnected || img.closest("[hidden]")) continue;
      const awaitingPreview = img.loading === "lazy" && !img.hasAttribute("loaded");
      let box = img.parentElement;
      while (box && getComputedStyle(box).display === "contents") box = box.parentElement;
      if (img.checkVisibility && (!img.checkVisibility({ visibilityProperty: true, opacityProperty: !awaitingPreview }) || !(box == null ? void 0 : box.checkVisibility({ visibilityProperty: true, opacityProperty: true }))))
        continue;
      const source = img.parentElement.tagName === "PICTURE" && [...img.parentElement.children].find(
        (node) => node.tagName === "SOURCE" && (!node.media || matchMedia(node.media).matches)
      );
      if ((_a3 = source == null ? void 0 : source.srcset) == null ? void 0 : _a3.startsWith("data:")) continue;
      const area = intersectionRect.width * intersectionRect.height;
      if (area > largestArea) {
        largest = img;
        largestArea = area;
      }
    }
    if (!largest) return;
    firstImage = largest;
    if (!ready && largest.dataset.progressiveImage !== "medium") promoteInitialPreview(largest);
    for (const img of images) {
      if (img === largest) {
        img.fetchPriority = "high";
        img.loading = "eager";
      } else if (img.fetchPriority === "high") img.fetchPriority = "auto";
    }
    queueInitialUpgrade(largest);
    schedule();
  });
  const stop = () => {
    initialObserver.disconnect();
    window.removeEventListener("pagehide", stop);
  };
  for (const img of images) initialObserver.observe(img);
  window.addEventListener("pagehide", stop, { once: true });
}
function queueInitialUpgrade(img) {
  if (ready || !img.matches(selector)) return;
  let idleHandle;
  const cleanup = () => {
    if (window.cancelIdleCallback) window.cancelIdleCallback(idleHandle);
    else clearTimeout(idleHandle);
    img.removeEventListener("load", queue);
    img.removeEventListener("error", queue);
    document.removeEventListener("visibilitychange", queue);
    window.removeEventListener("pagehide", cleanup);
    cancelInitialUpgrade = void 0;
  };
  const run = () => {
    idleHandle = void 0;
    if (document.hidden) return;
    cleanup();
    if (ready || !img.matches(selector) || !visible(img)) return;
    const rect = img.getBoundingClientRect();
    if (rect.bottom <= 0 || rect.top >= innerHeight || rect.right <= 0 || rect.left >= innerWidth) return;
    const candidate = candidateFor(img);
    if (!candidate) return;
    const state = states.get(img) || { dirty: true, applied: /* @__PURE__ */ new WeakMap() };
    if (state.loading || state.url === candidate.url || state.failed === candidate.url) return;
    states.set(img, state);
    upgrade(img, state, candidate);
  };
  const queue = () => {
    if (idleHandle !== void 0 || !img.complete || document.hidden) return;
    idleHandle = window.requestIdleCallback ? window.requestIdleCallback(run, { timeout: 2e3 }) : setTimeout(run, 200);
  };
  cancelInitialUpgrade = cleanup;
  img.addEventListener("load", queue);
  img.addEventListener("error", queue);
  document.addEventListener("visibilitychange", queue);
  window.addEventListener("pagehide", cleanup, { once: true });
  queue();
}
function previewSet(fullSet, cap) {
  const candidates = /* @__PURE__ */ new Map();
  for (const part of fullSet.split(",")) {
    const match = part.trim().match(/^(\S+)\s+(\d+)w$/);
    if (!match) continue;
    const url = new URL(match[1], document.baseURI);
    if (!url.searchParams.has("width")) continue;
    const width = Math.min(Number(match[2]), cap);
    url.searchParams.set("width", width);
    candidates.set(width, `${url.href} ${width}w`);
  }
  return [...candidates.values()].join(", ");
}
function promoteInitialPreview(img) {
  const fullSet = img.dataset.fullSrcset;
  if (!fullSet) return;
  const desktopSet = previewSet(fullSet, 480);
  const mobileSet = previewSet(fullSet, 140);
  if (!desktopSet || !mobileSet) return;
  let picture = img.parentElement.tagName === "PICTURE" ? img.parentElement : null;
  const sizes = (img.dataset.fullSizes || img.sizes || "100vw").replace(/^auto,\s*/, "");
  if (!picture) {
    picture = document.createElement("picture");
    picture.dataset.progressivePicture = "";
    picture.style.display = "contents";
    img.before(picture);
    picture.append(img);
  }
  for (const source of picture.querySelectorAll("source[data-full-srcset]")) {
    const cap = source.media.includes("max-width: 991.98px") ? 140 : 480;
    const preview = previewSet(source.dataset.fullSrcset, cap);
    if (!preview) continue;
    source.sizes = (source.dataset.fullSizes || sizes).replace(/^auto,\s*/, "");
    source.srcset = preview;
  }
  const mobile = document.createElement("source");
  mobile.dataset.progressiveMobile = "";
  mobile.media = "(max-width: 991.98px)";
  mobile.srcset = mobileSet;
  mobile.dataset.fullSrcset = fullSet;
  mobile.sizes = sizes;
  img.before(mobile);
  img.sizes = sizes;
  img.srcset = desktopSet;
  img.src = desktopSet.split(", ").at(-1).split(" ")[0];
  img.dataset.progressiveImage = "medium";
}
function cacheKey(url) {
  return warmCachePrefix + url;
}
function wasWarm(url) {
  try {
    return window.localStorage.getItem(cacheKey(url)) === "1";
  } catch {
    return false;
  }
}
function rememberWarm(url) {
  try {
    window.localStorage.setItem(cacheKey(url), "1");
  } catch {
  }
}
function clearIndicator(state) {
  var _a2;
  if (!state) return;
  clearTimeout(state.indicatorTimer);
  cancelAnimationFrame(state.indicatorFrame);
  (_a2 = state.indicator) == null ? void 0 : _a2.remove();
  state.indicator = null;
}
function positionIndicator(img, state) {
  cancelAnimationFrame(state.indicatorFrame);
  state.indicatorFrame = requestAnimationFrame(() => {
    const indicator = state.indicator;
    if (!(indicator == null ? void 0 : indicator.isConnected) || !img.isConnected) return;
    const rect = img.getBoundingClientRect();
    const parent = indicator.offsetParent;
    let left = rect.left + rect.width / 2 + scrollX;
    let top = rect.top + rect.height / 2 + scrollY;
    let scaleX = 1;
    if (parent && !(parent === document.body && getComputedStyle(parent).position === "static")) {
      const bounds = parent.getBoundingClientRect();
      scaleX = parent.offsetWidth ? bounds.width / parent.offsetWidth : 1;
      const scaleY = parent.offsetHeight ? bounds.height / parent.offsetHeight : 1;
      left = (rect.left + rect.width / 2 - bounds.left) / (scaleX || 1) + parent.scrollLeft - parent.clientLeft;
      top = (rect.top + rect.height / 2 - bounds.top) / (scaleY || 1) + parent.scrollTop - parent.clientTop;
    }
    indicator.style.left = `${left}px`;
    indicator.style.top = `${top}px`;
    indicator.style.width = `${Math.min(72, rect.width / (scaleX || 1) * 0.3)}px`;
    indicator.style.visibility = "visible";
  });
}
function queueIndicator(img, state, candidate) {
  if (wasWarm(candidate.url)) return;
  state.indicatorTimer = setTimeout(() => {
    var _a2;
    if (!state.loading || states.get(img) !== state || !visible(img)) return;
    const host = ((_a2 = img.parentElement) == null ? void 0 : _a2.tagName) === "PICTURE" ? img.parentElement.parentElement : img.parentElement;
    if (!host) return;
    const indicator = document.createElement("span");
    indicator.className = "progressive-image-loading";
    indicator.style.visibility = "hidden";
    indicator.setAttribute("aria-hidden", "true");
    host.append(indicator);
    state.indicator = indicator;
    positionIndicator(img, state);
  }, 80);
}
function visible(img) {
  if (!img.isConnected || document.hidden) return false;
  if (img.checkVisibility) {
    return img.checkVisibility({ visibilityProperty: true, opacityProperty: true });
  }
  return img.getClientRects().length > 0 && getComputedStyle(img).visibility !== "hidden";
}
function candidateFor(img) {
  var _a2;
  const picture = ((_a2 = img.parentElement) == null ? void 0 : _a2.tagName) === "PICTURE" ? img.parentElement : null;
  const source = picture && [...picture.children].find((el) => el.tagName === "SOURCE" && (!el.media || matchMedia(el.media).matches));
  const owner = source || img;
  const srcset = owner.getAttribute("data-full-srcset") || owner.getAttribute("srcset") || "";
  if (srcset.startsWith("data:")) return null;
  const candidates = srcset.split(",").map((part) => {
    const match = part.trim().match(/^(\S+)\s+(\d+)w$/);
    return match ? { url: match[1], width: Number(match[2]) } : null;
  }).filter(Boolean).sort((a, b) => a.width - b.width);
  const rect = img.getBoundingClientRect();
  const intrinsicWidth = Number(owner.getAttribute("width")) || img.naturalWidth;
  const intrinsicHeight = Number(owner.getAttribute("height")) || img.naturalHeight;
  const aspect = intrinsicHeight > 0 ? intrinsicWidth / intrinsicHeight : 0;
  const displayWidth = aspect > 0 && getComputedStyle(img).objectFit === "cover" ? Math.max(rect.width, rect.height * aspect) : rect.width;
  const width = displayWidth * (window.devicePixelRatio || 1);
  if (!width || !candidates.length) return null;
  const selected = candidates.find((item) => item.width >= width) || candidates.at(-1);
  return { ...selected, owner, url: new URL(selected.url, document.baseURI).href };
}
function schedule() {
  var _a2;
  if (scheduled || !ready || !interacted) return;
  scheduled = true;
  const run = () => {
    scheduled = false;
    pump();
  };
  if (!firstImage || !((_a2 = states.get(firstImage)) == null ? void 0 : _a2.url)) setTimeout(run, 0);
  else if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 250 });
  else setTimeout(run, 32);
}
async function upgrade(img, state, candidate) {
  state.loading = true;
  active++;
  queueIndicator(img, state, candidate);
  const probe = new Image();
  probe.decoding = "async";
  probe.fetchPriority = img === firstImage ? "high" : "low";
  if (img.crossOrigin) probe.crossOrigin = img.crossOrigin;
  probe.referrerPolicy = img.referrerPolicy;
  let timeout;
  try {
    const loaded = new Promise((resolve, reject) => {
      probe.onload = resolve;
      probe.onerror = reject;
      timeout = setTimeout(() => reject(new Error("Image upgrade timed out")), 15e3);
    });
    probe.src = candidate.url;
    await loaded;
    if (probe.decode) await probe.decode();
    if (!visible(img) || states.get(img) !== state) return;
    const current = candidateFor(img);
    if (!current || current.url !== candidate.url || current.owner !== candidate.owner) return;
    if (current.owner.getAttribute("data-full-srcset")) {
      current.owner.setAttribute("srcset", candidate.url);
    }
    if (current.owner === img) img.src = candidate.url;
    state.url = candidate.url;
    state.owner = candidate.owner;
    state.width = candidate.width;
    state.applied.set(candidate.owner, candidate);
    state.dirty = false;
    img.setAttribute("data-quality", "full");
    rememberWarm(candidate.url);
  } catch {
    if (states.get(img) === state && img.isConnected) {
      state.failed = candidate.url;
      failedImages.add(img);
    }
  } finally {
    clearIndicator(state);
    clearTimeout(timeout);
    probe.onload = probe.onerror = null;
    state.loading = false;
    active--;
    schedule();
  }
}
function pump() {
  if (!interacted || document.hidden || active >= 2) return;
  const work = [];
  const alreadySharp = [];
  for (const img of nearby) {
    const state = states.get(img);
    if (!(state == null ? void 0 : state.dirty) || state.loading || !img.complete || !visible(img)) continue;
    if (!observer) {
      const rect = img.getBoundingClientRect();
      if (rect.top > innerHeight + 160 || rect.bottom < -160 || rect.right < 0 || rect.left > innerWidth) continue;
    }
    if (!(firstImage == null ? void 0 : firstImage.isConnected)) {
      const rect = img.getBoundingClientRect();
      if (rect.top < innerHeight && rect.bottom > 0) firstImage = img;
    }
    const candidate = candidateFor(img);
    if (!candidate || candidate.url === state.failed) continue;
    const applied = state.applied.get(candidate.owner);
    if (applied && img.currentSrc === applied.url && applied.width >= candidate.width) {
      state.url = applied.url;
      state.owner = candidate.owner;
      state.width = applied.width;
      state.dirty = false;
      alreadySharp.push(img);
      continue;
    }
    if (img.currentSrc === candidate.url) {
      state.url = candidate.url;
      state.owner = candidate.owner;
      state.width = candidate.width;
      state.applied.set(candidate.owner, candidate);
      state.dirty = false;
      alreadySharp.push(img);
      continue;
    }
    work.push([
      img,
      state,
      candidate
    ]);
  }
  work.sort((a, b) => Number(b[0] === firstImage) - Number(a[0] === firstImage));
  for (const img of alreadySharp) {
    if (img.getAttribute("data-quality") !== "full") img.setAttribute("data-quality", "full");
  }
  for (const args of work.slice(0, 2 - active)) upgrade(...args);
}
function createObservers() {
  observer = "IntersectionObserver" in window ? new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          nearby.add(entry.target);
          const state = states.get(entry.target);
          if (state) state.dirty = true;
        } else nearby.delete(entry.target);
      }
      schedule();
    },
    { rootMargin: "160px 0px" }
  ) : null;
  resizeObserver = "ResizeObserver" in window ? new ResizeObserver((entries) => {
    for (const { target } of entries) {
      const state = states.get(target);
      if (state) state.dirty = true;
      if (state == null ? void 0 : state.indicator) positionIndicator(target, state);
    }
    schedule();
  }) : null;
}
function imagesIn(root) {
  var _a2;
  const images = [...root.querySelectorAll(selector)];
  if ((_a2 = root.matches) == null ? void 0 : _a2.call(root, selector)) images.unshift(root);
  return images;
}
function onImageLoad(event) {
  const img = event.target;
  const state = states.get(img);
  if (!state) return;
  state.dirty = true;
  if ((state == null ? void 0 : state.url) && img.currentSrc !== state.url) {
    img.removeAttribute("data-quality");
  }
  schedule();
}
function register(root) {
  const images = imagesIn(root);
  if (!images.length) return;
  for (const img of images) {
    const state = states.get(img) || { applied: /* @__PURE__ */ new WeakMap() };
    if (state.registered) continue;
    state.registered = true;
    state.dirty = true;
    state.failed = void 0;
    failedImages.delete(img);
    states.set(img, state);
    img.addEventListener("load", onImageLoad);
    img.addEventListener("error", schedule);
    if (observer) observer.observe(img);
    else nearby.add(img);
    resizeObserver == null ? void 0 : resizeObserver.observe(img);
  }
  schedule();
}
function releaseProgressiveImage(img) {
  var _a2;
  unregister(img);
  if (((_a2 = img.parentElement) == null ? void 0 : _a2.tagName) === "PICTURE") {
    img.parentElement.querySelectorAll("source[data-progressive-mobile]").forEach((source) => source.remove());
  }
  if (img.hasAttribute("data-full-sizes")) img.setAttribute("sizes", img.getAttribute("data-full-sizes"));
  for (const name of [
    "data-progressive-image",
    "data-full-srcset",
    "data-full-src",
    "data-full-sizes",
    "data-quality"
  ]) {
    img.removeAttribute(name);
  }
}
function refreshProgressiveImages() {
  schedule();
}
function observeProgressiveCarousel(api) {
  api.on("select", schedule);
  api.on("settle", schedule);
  api.on("destroy", () => {
    api.off("select", schedule);
    api.off("settle", schedule);
  });
}
function unregister(root) {
  for (const img of imagesIn(root)) {
    observer == null ? void 0 : observer.unobserve(img);
    resizeObserver == null ? void 0 : resizeObserver.unobserve(img);
    nearby.delete(img);
    failedImages.delete(img);
    clearIndicator(states.get(img));
    states.delete(img);
    img.removeEventListener("load", onImageLoad);
    img.removeEventListener("error", schedule);
    if (firstImage === img) {
      cancelInitialUpgrade == null ? void 0 : cancelInitialUpgrade();
      firstImage = void 0;
    }
  }
}
function onInteraction(event) {
  var _a2;
  if (!window.__bearPlaybackIntent && !((_a2 = window.Shopify) == null ? void 0 : _a2.designMode)) return;
  if (interacted && observer && (event.type === "scroll" || event.type === "wheel")) return;
  interacted = true;
  schedule();
}
function retryFailedImages(event) {
  if (event.isTrusted !== true || document.hidden || !interacted || !failedImages.size) return;
  for (const img of failedImages) {
    if (!nearby.has(img)) continue;
    const state = states.get(img);
    if (state) {
      state.failed = void 0;
      state.dirty = true;
    }
    failedImages.delete(img);
  }
  schedule();
}
function start() {
  var _a2;
  if (ready) return;
  cancelInitialUpgrade == null ? void 0 : cancelInitialUpgrade();
  interacted || (interacted = Boolean(window.__bearPlaybackIntent || ((_a2 = window.Shopify) == null ? void 0 : _a2.designMode)));
  createObservers();
  register(document);
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.removedNodes) {
        if (node.nodeType === 1 && !node.isConnected) unregister(node);
      }
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1 && node.isConnected) register(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
  for (const type of [
    "bear:playback-intent",
    "scroll"
  ]) {
    window.addEventListener(type, onInteraction, { passive: true });
  }
  for (const type of [
    "pointerdown",
    "touchstart",
    "keydown",
    "click",
    "wheel"
  ]) {
    window.addEventListener(type, retryFailedImages, { capture: true, passive: true });
  }
  document.addEventListener("transitionend", schedule, { passive: true });
  document.addEventListener("animationend", schedule, { passive: true });
  window.addEventListener(
    "resize",
    () => {
      for (const img of nearby) {
        const state = states.get(img);
        if (state) state.dirty = true;
      }
      schedule();
    },
    { passive: true }
  );
  document.addEventListener("visibilitychange", schedule);
  ready = true;
  schedule();
}
function startWhenPresent() {
  var _a2;
  if (!window.__bearPlaybackIntent && !((_a2 = window.Shopify) == null ? void 0 : _a2.designMode)) return;
  window.removeEventListener("bear:playback-intent", startWhenPresent);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start, { once: true });
  else setTimeout(start, 0);
}
window.addEventListener("bear:playback-intent", startWhenPresent);
if (document.readyState === "loading")
  document.addEventListener("DOMContentLoaded", selectInitialContentImage, { once: true });
else selectInitialContentImage();
startWhenPresent();
export {
  observeProgressiveCarousel,
  refreshProgressiveImages,
  releaseProgressiveImage
};
